import type { RealisationStep } from "./realisations-bank";

// Version anglaise des réalisations (realisations-bank.ts), affichée sur les
// pages en anglais : étude de cas des propositions, /en/realisations et
// /en/avis. Mêmes ids, mêmes champs affichés ; `captions` suit l'ordre de
// `screenshots`. Les témoignages sont des traductions du texte original,
// signalées comme telles à l'affichage. À tenir à jour avec la version
// française quand une réalisation change.

export interface RealisationEn {
  category: string;
  title: string;
  summary: string;
  client: string;
  role: string;
  context: string;
  steps: RealisationStep[];
  outcome: string;
  results: string;
  tags: string[];
  captions: string[];
  testimonial?: { quote: string; role: string };
}

export const REALISATIONS_EN: Record<string, RealisationEn> = {
  ezus: {
    category: "Business tool",
    title:
      "A Windows app that turns the Ezus API into ready-to-use CSV and Excel exports, with no code on the user's side",
    summary:
      "Travel agencies' data lives in Ezus: projects, clients, invoices. Getting it out used to take a developer or copy-and-paste. The tool now does it in a few clicks.",
    client: "A consulting firm that supports travel agencies using Ezus",
    role: "Developer of the application, from design to installer",
    context:
      "Ezus is management software for travel agencies that exposes its data through an API: projects, clients, suppliers, products, client and supplier invoices, travellers, project stages. The API is complete but unforgiving: two-step authentication, token pagination, strict quotas, and custom fields specific to each agency. Business teams could not use this data on their own.",
    steps: [
      {
        title: "A standalone Windows application",
        body: "An installer is all it takes, with no technical setup. Sign-in uses the Ezus email, password and API key. The email and key can be stored encrypted by Windows; the password is never kept. A demo mode lets users get started offline.",
      },
      {
        title: "Dynamic field discovery",
        body: "Across the eight modules, the application scans the records to list the standard fields and the agency's own custom fields, grouped by type and labelled in plain language.",
      },
      {
        title: "A preview before export",
        body: "Data is shown as readable cards suited to each module, or as a table. Search, filters, date range and row exclusion help prepare the export, and progressive rendering handles several thousand rows. A selection of modules, columns and filters can be saved as a template and replayed in one click.",
      },
      {
        title: "Clean CSV or Excel export",
        body: "UTF-8 CSV with BOM so accented characters display correctly in Excel, semicolon or comma separator, or a single Excel workbook with one tab per module. Custom file name, optional ISO 8601 timestamp and a history of previous exports.",
      },
      {
        title: "Respecting the API's constraints",
        body: "Full pagination, limits of 10,000 calls per day and 100 per second enforced with a counter kept across sessions, and errors reported clearly, including those the API returns under a success code.",
      },
    ],
    outcome:
      "Teams export their data without depending on a developer. Every export is reproducible, clean and ready for Excel or a reporting tool. API quotas are respected by design, and credentials are protected.",
    results: "8 modules covered by a single tool, 2 export formats (CSV and Excel), 55 automated tests.",
    tags: ["Desktop application", "API integration", "CSV / Excel export", "Business tool"],
    captions: [
      "Sign-in: Ezus email, password and API key, with a demo mode for getting started offline.",
      "Choosing the fields to export: standard fields and the agency's custom fields, reusable templates, format and separator of your choice.",
      "End of export: one file per module, ready to open, and the history of previous exports.",
    ],
    testimonial: {
      quote:
        "Flawless work. I was looking for a freelancer to build me a Windows export tool connected to an API (with access management, dynamic data sync and date filters to CSV). Thibault stepped up quickly. He was very responsive from start to finish and showed great professionalism. The result matches my needs and the solution runs perfectly. I am fully satisfied with our collaboration.",
      role: "Codeur.com client",
    },
  },
  "gold-merchandising": {
    category: "Project takeover",
    title:
      "Taking over a SaaS platform built with no-code: security audit, hardening and an exit from technical dependence",
    summary:
      "Take over the existing product without breaking what worked, close the critical security gaps, and give the client back control of their code and infrastructure.",
    client:
      "Gold Merchandising, an agency for in-store merchandising and fixture installation (historical client: Nocibé, prestige cosmetics)",
    role: "Full-stack developer & technical architect (takeover)",
    context:
      "Gold Merchandising's platform, a multi-role SaaS for managing in-store installation jobs, had been built on Lovable by a non-developer. It worked and was used in production, but it relied entirely on the no-code platform, with accumulated technical debt and several critical security flaws that had been reported but never verified.",
    steps: [
      {
        title: "Leaving the no-code platform for owned infrastructure",
        body: "Versioned Git repository, Docker images and multi-environment Coolify deployment (production, staging) on a Contabo VPS, with Supabase configuration injected when the container starts. The client now owns their code and their server, with no dependence on a third-party vendor.",
      },
      {
        title: "Security audit and fixes in production",
        body: "Full technical audit, then fixes for the flaws confirmed exploitable on the live database: privilege escalation (any signed-in account could declare itself administrator through the API), open read access to the users table, client prices and margins leaking to subcontractors. About 70 redundant RLS policies inherited from no-code were consolidated into one clear rule per table and per action.",
      },
      {
        title: "Bringing reliability up to standard",
        body: "Rebuilt job state machine (status derived by a PostgreSQL trigger), trigger-generated notifications, a pg_cron scheduled task for expired-document alerts, transactional stored procedures for pre-invoicing and client approval, file storage moved to a private S3 bucket with presigned URLs issued server-side after access checks, and Sentry set up.",
      },
      {
        title: "Passwordless authentication",
        body: "Switch to magic-link sign-in (no reusable secret left to steal or replay) and an impersonation feature reserved for administrators, for support.",
      },
      {
        title: "The \"2026 campaign update\" feature",
        body: "Multi-store campaigns with separate installation teams (each installer only sees their team's jobs), a guided reporting flow (before/after photos, point-of-sale materials, after-sales), schedule import from Excel (each row's colour sets the team), a trilingual FR/DE/EN interface, and read-only observer access for client-side contacts. The real campaign went live: 109 stores, 7 teams.",
      },
    ],
    outcome:
      "A platform no longer locked into a no-code vendor, whose code and infrastructure the client fully owns. Security flaws closed before they became costly, in particular margins leaking to outside contractors. A technical base that can take on new client campaigns without a rewrite.",
    results:
      "Around forty security and feature migrations delivered and applied in production, critical flaws closed and verified, a Nocibé campaign of 109 stores and 7 teams put online, a cleaned-up codebase (zero type errors, about 145 dead files removed).",
    tags: ["Project takeover", "Security audit", "Moving off no-code", "Field operations"],
    captions: [
      "Installer view: each installer only sees their team's jobs, never the full list.",
      "The guided reporting flow: before/after photos, point-of-sale materials, after-sales, step by step.",
      "Delivery screen: reporting the condition of the materials received by each store.",
    ],
  },
  lensmentor: {
    category: "Case study",
    title:
      "A photography learning SaaS driven by an AI mentor, from RAG architecture to production",
    summary:
      "Turning an educational vision held by a non-technical expert into a differentiated SaaS product, with an AI architecture designed so that every euro spent on generation serves perceived quality.",
    client:
      "LensMentor, a French photography learning platform (client: Marie-Ange Chabroullet, professional photographer)",
    role: "Full-stack developer & AI architect",
    context:
      "Marie-Ange wanted to turn her teaching expertise into a SaaS product: a platform that helps amateur photographers improve thanks to an AI mentor giving real photo critiques, tailored to each person's gear and level. The challenge was not plugging in a model but building real product differentiation: a mentor that understands the student's context.",
    steps: [
      {
        title: "Custom AI architecture",
        body: "End-to-end design of the AI mentor, powered by Claude (Anthropic). A layered prompt-caching architecture (system, course content, camera specifications, user context, history) to optimise both the relevance of answers and the cost per request.",
      },
      {
        title: "RAG and knowledge base",
        body: "Retrieval-augmented generation over two sources (lesson content and photo critique history), indexed with pgvector on Neon and Voyage AI embeddings. A structured critique grid combining universal criteria and mode-specific criteria, across three adaptive difficulty levels.",
      },
      {
        title: "Camera database",
        body: "A database of camera bodies (Canon, Nikon, Sony, Fujifilm, Panasonic, Olympus, Pentax since 2015) that tailors advice to the actual gear: ISO, shutter speed and exposure compensation settings bounded by the camera's capabilities.",
      },
      {
        title: "Gamification and learning paths",
        body: "Seven structured paths, a unified XP system where the level declared at onboarding sets the starting point, automatically generated weekly recommendations, and anti-abuse safeguards on XP actions.",
      },
    ],
    outcome:
      "A working, differentiated SaaS product whose AI architecture is ready to grow without generation costs exploding as usage increases.",
    results:
      "First milestones approved and invoiced, AI architecture ready to be extended, product in active development.",
    tags: ["SaaS", "AI mentor (RAG)", "Gamification", "Marketplace"],
    captions: [
      "The AI mentor: answers framed by the chosen mode and the specifications of the student's camera.",
      "The learning paths, split into lessons with progress tracking and difficulty levels.",
      "The back office: members, revenue, activity, tracking of AI generation costs.",
    ],
    testimonial: {
      quote:
        "Thibault built the LensMentor SaaS platform from scratch and the experience was excellent from start to finish. First, his responsiveness: answers come quickly and check-ins are regular, you are never left waiting. Second, he brings ideas. He doesn't just execute a specification, he challenges it, proposes relevant technical solutions and adds real value on architecture choices. Third strength: excellent development skills, with clean and reliable code. But what I appreciated most is his human side and his ability to listen. He understands what is at stake in a founder's project, adapts, and makes the collaboration smooth and pleasant. I recommend Thibault without hesitation and I keep working with him.",
      role: "Founder & CEO, LensMentor",
    },
  },
  lcde: {
    category: "Rebuild",
    title:
      "Complete rebuild of a community platform: from WordPress to a custom Next.js architecture with an AI assistant",
    summary:
      "Taking over a WordPress site overloaded with plugins, understanding its business logic, and rebuilding it on solid foundations without ever losing touch with the product's business reality.",
    client: "La Communauté des Entrepreneurs, a French-speaking platform dedicated to entrepreneurship",
    role: "Full-stack developer & technical architect (technical co-founder of the project)",
    context:
      "LCDE ran on WordPress with a stack of plugins (BuddyBoss, WooCommerce, a custom chatbot plugin). The founder, who is not technical, needed a partner able to take over the existing product, understand its business logic and rebuild it on solid foundations.",
    steps: [
      {
        title: "Audit and extraction of the existing product",
        body: "Before any rewrite, the in-house chatbot plugin (PHP + React) was taken apart to extract its real business logic: system prompt, triggers, user journey. The goal: lose nothing of what already worked.",
      },
      {
        title: "Complete technical rebuild",
        body: "Migration from WordPress to Next.js 14, a Neon database (serverless PostgreSQL), Drizzle ORM, Stripe payments, Brevo emailing, GA4 analytics, Coolify hosting on a dedicated VPS.",
      },
      {
        title: "Alfred, the platform's AI assistant",
        body: "Complete rebuild of the chatbot: a move to Mistral AI, a RAG architecture connected to three sources (user profile, platform resources indexed with embeddings, private knowledge base), and an identity consistent with the brand's tone.",
      },
      {
        title: "Notifications and user journey",
        body: "A complete notification system (in-app + email), a nine-step structured journey to guide members, and gamification logic to maximise engagement.",
      },
    ],
    outcome:
      "A platform no longer dependent on a pile of fragile WordPress plugins, an AI assistant that really knows the community's content and members, and a technical base designed to evolve without starting from scratch for every new feature.",
    results: "V2 platform delivered and in production, rebuild carried out over several months, on schedule.",
    tags: ["Rebuild / migration", "AI assistant (RAG)", "Notifications", "Gamification"],
    captions: [
      "The resource library: articles, videos, podcasts and documents indexed on the nine steps of the journey.",
      "The community space: feed, topic forums, active members and introductions.",
      "Alfred, the platform's AI assistant, with its own identity and a RAG connected to three sources.",
    ],
    testimonial: {
      quote:
        "I entrusted Thibault with the complete rebuild of V2 of my platform, over several months. The result exceeds my expectations. Technically, his choices were solid at every stage, with clean code and real command of the technologies. He was able to step back on the architecture, avoiding a lot of technical debt. Above all, his autonomy struck me: never any need to chase him, he anticipated and came back with solutions. Deadlines met. I recommend Thibault without hesitation.",
      role: "Founder and managing director, La Communauté des Entrepreneurs",
    },
  },
  cheeze: {
    category: "Operations dashboard",
    title:
      "A dashboard that drives the financial and operational performance of a network of more than 30 dental clinics",
    summary:
      "A consolidated view of the network, clinic by clinic and practitioner by practitioner, where manual spreadsheets only gave partial and out-of-date snapshots.",
    client: "The Dental Network SA (Cheeze), a network of more than 30 dental clinics in Switzerland",
    role: "Full-stack developer",
    context:
      "A network of more than 30 dental clinics needs to track, in one place and with the same figures, revenue performed and invoiced, discounts granted and each practitioner's performance. Without a dedicated platform, this tracking relied on exports and spreadsheets rebuilt clinic by clinic.",
    steps: [
      {
        title: "Consolidated financial tracking",
        body: "Net and gross revenue performed and invoiced, compared with the network average, aggregated by clinic and by month, with performance history over several rolling months.",
      },
      {
        title: "Discount analysis",
        body: "Breakdown of discounts granted by role (specialist, dentist, hygienist) and by practitioner, to spot pricing drift before it weighs on margin.",
      },
      {
        title: "Managing schedule occupancy",
        body: "Schedule occupancy compared with full-time equivalents and actual availability, by clinic, to ground hiring and organisation decisions in data.",
      },
      {
        title: "Individual practitioner performance",
        body: "Revenue per open hour and per available hour, target achievement, and appointment indicators per practitioner, for fine-grained management without manual re-entry.",
      },
    ],
    outcome:
      "A single platform where every clinic in the network is managed with the same up-to-date indicators, without rebuilding a spreadsheet at the end of each month.",
    results:
      "Dashboard used continuously by the network to manage its activity, with a performance history that grows month after month.",
    tags: ["Ops dashboard", "Financial reporting", "Performance analysis", "Multi-site"],
    captions: [],
    testimonial: {
      quote:
        "I highly recommend Thibault for his exceptional programming skills. He stands out for his excellent ability to execute, combined with a fine understanding of the operational environment in which the solutions he develops are used. Beyond the technical quality of his work, Thibault quickly grasps the business stakes and proposes suitable, robust and effective solutions. His experience across a wide range of subjects, all successfully delivered, shows his rigour, reliability, versatility and commitment.",
      role: "Knief Clerc Advisory",
    },
  },
  flashposter: {
    category: "Founder project",
    title: "An AI SaaS for blog content creation, designed, built and launched solo",
    summary:
      "Carrying a SaaS product alone, from design to operations: architecture decisions, full development, deployment and running it without a team.",
    client: "FlashPoster, an AI-powered SaaS tool for creating blog posts",
    role: "Founder & full-stack developer (founder project, now inactive)",
    context:
      "Producing quality blog content at a steady pace takes writing and SEO expertise that many teams don't have in-house. The idea behind FlashPoster: let anyone go from inspiration to publication in three clicks, with content optimised for search.",
    steps: [
      {
        title: "Product design and independent launch",
        body: "From idea to product in production: framing the need, designing the architecture, development and deployment, all carried alone, end to end.",
      },
      {
        title: "AI-assisted content generation",
        body: "Integration of the OpenAI API to generate SEO-optimised blog articles, with a flow designed to remove as much friction as possible between the initial idea and the published article.",
      },
      {
        title: "Production infrastructure",
        body: "Python / Flask backend, React / TypeScript frontend, MongoDB database, VPS deployment with a CI/CD pipeline: a stack designed to handle continuous content generation.",
      },
      {
        title: "Iterating on performance",
        body: "Optimisation of the generation pipeline to cut processing times, with a constant focus on the platform's reliability in production.",
      },
    ],
    outcome:
      "A concrete demonstration of the ability to carry a SaaS product alone, from the first line of code to operations.",
    results:
      "2,500 articles generated and published on WordPress, 99.5% uptime in production, generation pipeline about 80% faster.",
    tags: ["SaaS", "AI generation", "SEO", "Solo founder"],
    captions: [
      "The generated article editor: structure, images, SEO metadata and export, ready to publish.",
      "Generation settings: template, length, internal and external links, publishing to the site.",
    ],
  },
  "prospection-b2b": {
    category: "Automation",
    title: "An automated B2B prospecting pipeline targeting 103,000 companies, delivered in ten days",
    summary:
      "Proving that a large-scale prospecting system can be delivered quickly and without heavy development, with a no-code/low-code architecture suited to a tight budget and deadline.",
    client: "A search fund based in Geneva",
    role: "Automation architect & developer",
    context:
      "A Geneva search fund needed to identify and qualify acquisition targets among the canton's SMEs, with no budget for heavy development and no time to hire a data team.",
    steps: [
      {
        title: "Building the target database",
        body: "Extraction and cleaning of a database of 103,000 Geneva companies from SITG public data, filtered by NOGA code to target the relevant sectors.",
      },
      {
        title: "Multi-source enrichment",
        body: "A pipeline combining Zefix (the Swiss commercial register), the Geneva commercial register API through a Swiss residential proxy, Google Places and LinkedIn, to complete each company record with reliable data.",
      },
      {
        title: "AI scoring and summaries",
        body: "Kimi K2.5 used to generate company summaries and personalised outreach messages, replacing manual qualification work that would be impossible at this scale.",
      },
      {
        title: "Orchestration and CRM",
        body: "End-to-end automation with n8n, data centralised in Teable then Attio (CRM), outreach run through Lemlist. Everything connected by an architecture designed to stay maintainable without an in-house technical team.",
      },
    ],
    outcome:
      "A large-scale prospecting system delivered quickly, without sacrificing either target coverage or enrichment quality.",
    results:
      "Pipeline in production, able to process a database of 103,000 companies, the most complex automation project handled by the agency.",
    tags: ["Data pipeline", "n8n automation", "Enrichment", "AI scoring"],
    captions: [
      "One of the n8n workflows: enriching a company director from LinkedIn, with search variants and AI analysis.",
      "The target database centralised in Teable: 103,000 companies filtered by NOGA code, ready for enrichment.",
    ],
  },
};
